import React, { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import Layout from '../components/Layout';
import * as pushNotificationService from '../api/pushNotificationService';
import type { VerifyCheck } from '../api/pushNotificationService';

const statusConfig = {
  pass: {
    bg: 'bg-green-50',
    border: 'border-green-200',
    icon: 'text-green-500',
    label: 'Pass',
    labelBg: 'bg-green-100 text-green-800',
  },
  fail: {
    bg: 'bg-red-50',
    border: 'border-red-200',
    icon: 'text-red-500',
    label: 'Fail',
    labelBg: 'bg-red-100 text-red-800',
  },
  warn: {
    bg: 'bg-yellow-50',
    border: 'border-yellow-200',
    icon: 'text-yellow-500',
    label: 'Warning',
    labelBg: 'bg-yellow-100 text-yellow-800',
  },
  info: {
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    icon: 'text-blue-500',
    label: 'Info',
    labelBg: 'bg-blue-100 text-blue-800',
  },
};

const StatusIcon: React.FC<{ status: string }> = ({ status }) => {
  const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.info;
  if (status === 'pass') {
    return (
      <svg className={`h-6 w-6 ${config.icon}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    );
  }
  if (status === 'fail') {
    return (
      <svg className={`h-6 w-6 ${config.icon}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    );
  }
  if (status === 'warn') {
    return (
      <svg className={`h-6 w-6 ${config.icon}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
    );
  }
  return (
    <svg className={`h-6 w-6 ${config.icon}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
};

const CheckCard: React.FC<{ check: VerifyCheck }> = ({ check }) => {
  const config = statusConfig[check.status] || statusConfig.info;

  return (
    <div className={`rounded-lg border ${config.border} ${config.bg} p-4`}>
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 mt-0.5">
          <StatusIcon status={check.status} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="text-sm font-semibold text-gray-900">{check.name}</h4>
            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${config.labelBg}`}>
              {config.label}
            </span>
          </div>
          <p className="mt-1 text-sm text-gray-700">{check.details}</p>

          {check.items && check.items.length > 0 && (
            <div className="mt-3 space-y-1.5">
              {check.items.map((item) => (
                <div key={item.name} className="flex items-center gap-2 text-xs">
                  {item.set ? (
                    <svg className="h-4 w-4 text-green-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <svg className="h-4 w-4 text-red-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  )}
                  <span className="font-mono text-gray-600">{item.name}</span>
                  {item.masked && (
                    <span className="text-gray-500">= {item.masked}</span>
                  )}
                </div>
              ))}
            </div>
          )}

          {check.breakdown && (
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
              {Object.entries(check.breakdown).map(([key, value]) => {
                if (value && typeof value === 'object') return null;
                return (
                  <div key={key} className="bg-white bg-opacity-60 rounded px-2 py-1.5">
                    <div className="text-xs text-gray-500 capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</div>
                    <div className="text-sm font-semibold text-gray-900">{typeof value === 'number' ? value : String(value)}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const PushNotificationSetup: React.FC = () => {
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  const { data: verifyData, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['push-notification-verify'],
    queryFn: () => pushNotificationService.verifySetup(),
  });

  const testMutation = useMutation({
    mutationFn: () => pushNotificationService.sendTestNotification(),
    onSuccess: (data) => {
      setTestResult({ success: true, message: data.message || 'Test notification sent successfully' });
    },
    onError: (error: any) => {
      setTestResult({
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to send test notification',
      });
    },
  });

  const result = verifyData?.data;

  const overallBanner = () => {
    if (!result) return null;
    const { status, message, counts } = result.overall;

    let bgClass = 'bg-green-600';
    let iconPath = 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z';
    if (status === 'error') {
      bgClass = 'bg-red-600';
      iconPath = 'M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z';
    } else if (status === 'warning') {
      bgClass = 'bg-yellow-500';
      iconPath = 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z';
    }

    return (
      <div className={`${bgClass} rounded-lg shadow-sm p-5 text-white`}>
        <div className="flex items-center gap-3">
          <svg className="h-8 w-8 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={iconPath} />
          </svg>
          <div>
            <h2 className="text-lg font-bold">{message}</h2>
            <p className="text-sm opacity-90 mt-0.5">
              {counts.pass} passed, {counts.fail} failed, {counts.warn} warning(s)
              {result.verifiedAt && ` — verified ${new Date(result.verifiedAt).toLocaleString()}`}
            </p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Layout>
      <div className="px-4 sm:px-6 lg:px-8 py-8">
        <div className="sm:flex sm:items-center sm:justify-between">
          <div className="sm:flex-auto">
            <h1 className="text-3xl font-bold text-gray-900">Push Notification Setup</h1>
            <p className="mt-2 text-sm text-gray-700">
              Verify that iOS push notifications are configured correctly
            </p>
          </div>
          <div className="mt-4 sm:mt-0 flex gap-3">
            <button
              onClick={() => testMutation.mutate()}
              disabled={testMutation.isPending || !result?.overall || result.overall.status === 'error'}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {testMutation.isPending ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-500" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Sending...
                </>
              ) : (
                <>
                  <svg className="-ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                  Send Test
                </>
              )}
            </button>
            <button
              onClick={() => { setTestResult(null); refetch(); }}
              disabled={isFetching}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
            >
              {isFetching ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Checking...
                </>
              ) : (
                <>
                  <svg className="-ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Re-verify
                </>
              )}
            </button>
          </div>
        </div>

        {/* Test result alert */}
        {testResult && (
          <div className={`mt-6 rounded-md p-4 ${testResult.success ? 'bg-green-50' : 'bg-red-50'}`}>
            <div className="flex">
              <div className="flex-shrink-0">
                {testResult.success ? (
                  <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                )}
              </div>
              <div className="ml-3 flex-1">
                <p className={`text-sm font-medium ${testResult.success ? 'text-green-800' : 'text-red-800'}`}>
                  {testResult.message}
                </p>
              </div>
              <button onClick={() => setTestResult(null)} className="ml-3 text-gray-400 hover:text-gray-500">
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {isLoading ? (
          <div className="mt-8 text-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-sm text-gray-500">Running verification checks...</p>
          </div>
        ) : result ? (
          <>
            {/* Overall status banner */}
            <div className="mt-6">
              {overallBanner()}
            </div>

            {/* Verification checks */}
            <div className="mt-6 space-y-4">
              {result.checks.map((check) => (
                <CheckCard key={check.id} check={check} />
              ))}
            </div>

            {/* iOS Setup Checklist */}
            <div className="mt-8 bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg font-medium text-gray-900">iOS App Setup Checklist</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Ensure these steps are completed in the iOS app and Apple Developer Portal
                </p>
                <div className="mt-4 space-y-3">
                  {[
                    {
                      title: 'Enable Push Notifications capability in Xcode',
                      description: 'Target > Signing & Capabilities > + Capability > Push Notifications',
                    },
                    {
                      title: 'Enable Background Modes > Remote Notifications in Xcode',
                      description: 'Target > Signing & Capabilities > Background Modes > Remote notifications',
                    },
                    {
                      title: 'Create APNs Auth Key in Apple Developer Portal',
                      description: 'Certificates, Identifiers & Profiles > Keys > Create a key with APNs enabled',
                    },
                    {
                      title: 'Download the .p8 key file and place it on the server',
                      description: 'Set APNS_KEY_PATH to the absolute path of the .p8 file',
                    },
                    {
                      title: 'Set APNS_KEY_ID to the Key ID from Apple Developer Portal',
                      description: 'Found on the Keys page next to the key name',
                    },
                    {
                      title: 'Set APNS_TEAM_ID to your Apple Team ID',
                      description: 'Found in Membership Details or the App ID prefix',
                    },
                    {
                      title: 'Set APNS_BUNDLE_ID to match the iOS app bundle identifier',
                      description: 'Must match exactly — e.g., com.242network.hub',
                    },
                    {
                      title: 'Request notification permissions in the iOS app',
                      description: 'UNUserNotificationCenter.requestAuthorization and register for remote notifications',
                    },
                    {
                      title: 'Send device token to backend API',
                      description: 'POST /api/push-notifications/register with the device token from didRegisterForRemoteNotificationsWithDeviceToken',
                    },
                    {
                      title: 'Set APNS_PRODUCTION=true for App Store / TestFlight builds',
                      description: 'Development builds use sandbox, production/TestFlight builds use the production APNs gateway',
                    },
                  ].map((item, index) => (
                    <div key={index} className="flex items-start gap-3 p-3 rounded-lg bg-gray-50">
                      <div className="flex-shrink-0 mt-0.5">
                        <span className="flex items-center justify-center h-6 w-6 rounded-full bg-gray-200 text-xs font-bold text-gray-600">
                          {index + 1}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{item.title}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{item.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="mt-8 text-center py-12 bg-white rounded-lg shadow">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">Verification Failed</h3>
            <p className="mt-1 text-sm text-gray-500">Could not retrieve verification data. Check the backend connection.</p>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default PushNotificationSetup;
